import { DOCUMENT } from '@angular/common';
import { inject, provideAppInitializer } from '@angular/core';

const INVALID_CONTROL_SELECTOR = [
  'input.ng-invalid:not([type="hidden"]):not([disabled])',
  'select.ng-invalid:not([disabled])',
  'textarea.ng-invalid:not([disabled])',
  '[formControlName].ng-invalid',
  '[formControl].ng-invalid',
  '[ngModel].ng-invalid',
].join(', ');

export function provideFocusFirstInvalidField() {
  return provideAppInitializer(() => {
    installFocusFirstInvalidOnSubmit(inject(DOCUMENT));
  });
}

export function focusFirstInvalidField(root: ParentNode): boolean {
  const control = findFirstInvalidControl(root);
  if (!control) {
    return false;
  }

  const target = resolveFocusable(control);
  const scroller = control.closest('div') ?? control;
  scroller.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
    inline: 'nearest',
  });

  if (target && typeof target.focus === 'function') {
    target.focus({ preventScroll: true });
  }

  return true;
}

function installFocusFirstInvalidOnSubmit(document: Document): void {
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) {
        return;
      }

      queueMicrotask(() => {
        requestAnimationFrame(() => {
          focusFirstInvalidField(form);
        });
      });
    },
    true,
  );
}

function findFirstInvalidControl(root: ParentNode): HTMLElement | null {
  const candidates = root.querySelectorAll(INVALID_CONTROL_SELECTOR);

  for (let index = 0; index < candidates.length; index += 1) {
    const node = candidates.item(index);
    if (!(node instanceof HTMLElement) || node.tagName === 'FORM') {
      continue;
    }

    if (!isDisplayed(node)) {
      continue;
    }

    return node;
  }

  return null;
}

function resolveFocusable(el: HTMLElement): HTMLElement | null {
  if (el.matches('input, select, textarea') && isDisplayed(el)) {
    return el;
  }

  const inner = el.querySelector<HTMLElement>(
    'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])',
  );

  if (inner && isDisplayed(inner)) {
    return inner;
  }

  return el;
}

function isDisplayed(el: HTMLElement): boolean {
  return el.getClientRects().length > 0;
}
